import type {PoolEntry} from "../../../../domain/gimmicks/employee-pool";

/** Where a draw stands: none yet, the looks moving towards the result, the result shown. */
export type PickerPhase = 'idle' | 'spinning' | 'shown';

/** What every look of the picker draws from (feature plan 0072). */
export interface PickerSkinInput {
    /** All active employees, sorted; never empty here (the element shows a notice instead). */
    readonly pool: readonly PoolEntry[];
    /** The result of the current draw; `null` before the first. */
    readonly current: PoolEntry | null;
    /** The result of the draw before — wheel and reel start where it left them. */
    readonly previous: PoolEntry | null;
    /** 0 … 1, already eased (`frame-tween.ts`): its first half passes in about a fifth of the time. A look's
     *  sections are shares of this progress, not of the time. 1 once shown. */
    readonly progress: number;
    readonly phase: PickerPhase;
}

/** The index of `entry` in the pool, `null` when absent (no draw yet). */
export function indexIn(pool: readonly PoolEntry[], entry: PoolEntry | null): number | null {
    if (entry === null) {
        return null;
    }
    const index = pool.findIndex((candidate) => candidate.uid === entry.uid);
    return index < 0 ? null : index;
}

/** How many characters of a shortcut fit on a ball, a wheel segment or a card — a tenant may keep shortcuts of
 *  up to 10 characters (`Shortcutable`), the initials fallback has 2 (review 0072-04). */
export const SHORTCUT_FIT = 4;

/** SVG text cannot ellipsize: a label longer than `max` characters is cut and ends in "…". */
export function fitLabel(text: string, max: number): string {
    const chars = Array.from(text);
    return chars.length <= max ? text : `${chars.slice(0, max - 1).join('').trimEnd()}…`;
}

/** `a mod n` in `0 … n-1`, also for negative `a`. */
export function mod(a: number, n: number): number {
    return ((a % n) + n) % n;
}

export function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}
